// filepath: src/pages/ProductList.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
// import tinycolor from "tinycolor2";

import SearchInput from "../components/CommonComponents/SearchInput";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";
import AddProductModal from "../components/admin/products/AddProductModal";
import ProductGrid from "../components/admin/products/ProductGrid";
import ProductTable from "../components/admin/products/ProductTable";
import ViewToggle from "../components/CommonComponents/ViewToggle";
import Pagination from "../components/CommonComponents/Pagination";
import EmptyState from "../components/CommonComponents/EmptyState";
import TableLoadingSkeleton from "../components/CommonComponents/TableLoadingSkeleton";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { Package } from "lucide-react";
import PrimaryButton from "../components/CommonComponents/PrimaryButton";
import { authStorage } from "../utils/authStorage";

const ProductList = () => {
    const storedUser = authStorage.getUser();
    const isManager = storedUser?.user_type === 2
        && String(storedUser?.role_name || "").toLowerCase() === "manager";
    const canManageProducts = !isManager;
    // ...existing code...
    // -------- State --------
    const [products, setProducts] = useState([]);
    const [branches, setBranches] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState({ type: null, product: null, open: false });
    const [viewMode, setViewMode] = useState("table"); // "table" or "grid"
    const [selectedBranchId, setSelectedBranchId] = useState("");
    const productsPerPage = 10;

    const baseURL = `${process.env.REACT_APP_API_URL}/api/admin/products`;
    const uploadsBaseURL = `${process.env.REACT_APP_API_URL}/uploads`;
    const branchesURL = `${process.env.REACT_APP_API_URL}/api/admin/branches`;
    const categoriesURL = `${process.env.REACT_APP_API_URL}/api/admin/products/categories`;

    // ---------------- Axios Auth Helper ----------------
    const getAuthHeaders = () => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) throw new Error("Authorization token missing.");
        return { Authorization: `Bearer ${token}` };
    };

    // -------- Fetch Products --------
    const loadProducts = useCallback(async (page = 1, search = "") => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: productsPerPage };
            if (search) params.search = search;
            if (categoryFilter) params.category = categoryFilter;
            if (selectedBranchId) params.price_branch_id = selectedBranchId;
            const res = await axios.get(baseURL, { headers: getAuthHeaders(), params });
            const normalized = (res.data.products || []).map((product) => {
                const branchPrice = product.branch_price ?? product.branchPrices?.[0]?.price ?? null;
                const effectivePrice = branchPrice ?? product.effective_price ?? product.price;
                return {
                    ...product,
                    branch_price: branchPrice,
                    effective_price: effectivePrice,
                };
            });
            setProducts(normalized);
            setTotalPages(res.data.totalPages || 1);
            setTotalProducts(res.data.total || 0);
        } catch (err) {
            console.error(err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [productsPerPage, baseURL, selectedBranchId, categoryFilter]);

    const loadBranches = useCallback(async () => {
        try {
            const res = await axios.get(branchesURL, { headers: getAuthHeaders() });
            const data = Array.isArray(res.data?.data) ? res.data.data : [];
            const activeBranches = data.filter((branch) => String(branch?.status || "ACTIVE") === "ACTIVE");
            setBranches(activeBranches);
        } catch (err) {
            console.error("Failed to load branches:", err);
            setBranches([]);
        }
    }, [branchesURL]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await axios.get(categoriesURL, { headers: getAuthHeaders() });
            const data = Array.isArray(res.data?.categories) ? res.data.categories : [];
            setCategories(data);
        } catch (err) {
            console.error("Failed to load categories:", err);
            setCategories([]);
        }
    }, [categoriesURL]);

    useEffect(() => {
        loadProducts(currentPage, searchTerm);
    }, [loadProducts, currentPage, searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedBranchId, categoryFilter]);

    useEffect(() => {
        loadBranches();
    }, [loadBranches]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    // ---------------- Pagination Handlers ----------------
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };


    // ---------------- Modal Helpers ----------------
    const openModal = (type, product = null) => {
        if (type !== "edit" || !product) {
            setModal({ type, product, open: true });
            return;
        }

        const branchId = selectedBranchId ? String(selectedBranchId) : "";
        const branchPrice = selectedBranchId
            ? (product.branch_price ?? product.branchPrices?.[0]?.price ?? product.effective_price ?? "")
            : "";

        setModal({
            type,
            open: true,
            product: {
                ...product,
                branch_id: branchId,
                branch_price: branchPrice === "" ? "" : String(branchPrice),
                previous_branch_id: branchId,
            },
        });
    };
    const closeModal = () => setModal({ type: null, product: null, open: false });

    // ---------------- Delete Product ----------------
    const handleDeleteProduct = async () => {
        if (!modal.product) return;
        try {
            await axios.delete(`${baseURL}/${modal.product.id}`, { headers: getAuthHeaders() });
            setProducts((prev) => prev.filter((p) => p.id !== modal.product.id));
            closeModal();
            showSuccessToast("Product deleted successfully.");
        } catch (err) {
            console.error(err);
            showErrorToast(err.response?.data?.message || "Failed to delete product.");
        }
    };

    // ---------------- Add / Edit Product ----------------
    const handleAddEditProduct = async (formData, isEdit = false) => {
        const url = isEdit ? `${baseURL}/${modal.product.id}` : baseURL;
        try {
            const res = isEdit
                ? await axios.put(url, formData, {
                    headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
                })
                : await axios.post(url, formData, {
                    headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
                });

            showSuccessToast(res.data.message || (isEdit ? "Product updated" : "Product added"));
            loadProducts(currentPage, searchTerm);
            closeModal();
        } catch (err) {
            console.error(err);
            showErrorToast(err.response?.data?.message || (isEdit ? "Failed to update" : "Failed to add"));
        }
    };

    const showBranchPrice = Boolean(selectedBranchId);
    const branchOptions = useMemo(() => {
        const base = [{ value: "", label: "All branches (universal price)" }];
        const branchItems = branches.map((branch) => ({
            value: String(branch.id),
            label: branch.name,
        }));
        return [...base, ...branchItems];
    }, [branches]);

    const selectedBranchOption = useMemo(() => {
        return branchOptions.find((opt) => String(opt.value) === String(selectedBranchId)) || branchOptions[0];
    }, [branchOptions, selectedBranchId]);

    const reactSelectStyles = useMemo(() => ({
        control: (base) => ({
            ...base,
            minHeight: 36,
            borderRadius: 8,
            borderColor: "#d1d5db",
            boxShadow: "none",
            fontSize: 14,
        }),
        menu: (base) => ({ ...base, borderRadius: 10, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            background: state.isSelected ? "#e5e7eb" : state.isFocused ? "#f3f4f6" : "#fff",
            color: "#111827",
        }),
    }), []);

    const categoryOptions = useMemo(() => {
        const base = [{ value: "", label: "All categories" }];
        const items = (categories || []).map((cat) => ({ value: cat, label: cat }));
        if (categoryFilter && !(categories || []).includes(categoryFilter)) {
            items.unshift({ value: categoryFilter, label: categoryFilter });
        }
        return [...base, ...items];
    }, [categories, categoryFilter]);

    const selectedCategoryOption = useMemo(() => {
        return categoryOptions.find((opt) => String(opt.value) === String(categoryFilter)) || categoryOptions[0];
    }, [categoryOptions, categoryFilter]);
    const productSkeletonConfig = useMemo(() => {
        if (canManageProducts) {
            return {
                columnLabels: [
                    "Image",
                    "Code",
                    "Name",
                    "Category",
                    "Universal Price",
                    ...(showBranchPrice ? ["Branch Price"] : []),
                    "Unit",
                    "Created",
                    "Updated",
                    "Actions",
                ],
                columnTypes: [
                    "image",
                    "text",
                    "text",
                    "text",
                    "text",
                    ...(showBranchPrice ? ["text"] : []),
                    "text",
                    "text",
                    "text",
                    "actions",
                ],
                columnTemplate: showBranchPrice
                    ? "56px 1.1fr 1.6fr 1.2fr 1fr 1fr 0.9fr 1.1fr 1.1fr 0.9fr"
                    : "56px 1.1fr 1.6fr 1.2fr 1fr 0.9fr 1.1fr 1.1fr 0.9fr",
            };
        }
        return {
            columnLabels: [
                "Image",
                "Code",
                "Name",
                "Category",
                "Universal Price",
                ...(showBranchPrice ? ["Branch Price"] : []),
                "Unit",
                "Created",
                "Updated",
            ],
            columnTypes: [
                "image",
                "text",
                "text",
                "text",
                "text",
                ...(showBranchPrice ? ["text"] : []),
                "text",
                "text",
                "text",
            ],
            columnTemplate: showBranchPrice
                ? "56px 1.1fr 1.6fr 1.2fr 1fr 1fr 0.9fr 1.1fr 1.1fr"
                : "56px 1.1fr 1.6fr 1.2fr 1fr 0.9fr 1.1fr 1.1fr",
        };
    }, [canManageProducts, showBranchPrice]);

    // ---------------- Render ----------------
    return (
        <div className="pt-6">
            {/* Search + Add Button */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <SearchInput
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search by product name..."
                />
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="min-w-[200px]">
                        <Select
                            classNamePrefix="react-select"
                            styles={reactSelectStyles}
                            options={categoryOptions}
                            value={selectedCategoryOption}
                            onChange={(opt) => setCategoryFilter(opt?.value || "")}
                            isSearchable={false}
                            placeholder="All categories"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    <div className="min-w-[220px]">
                        <Select
                            classNamePrefix="react-select"
                            styles={reactSelectStyles}
                            options={branchOptions}
                            value={selectedBranchOption}
                            onChange={(opt) => setSelectedBranchId(opt?.value || "")}
                            isSearchable={false}
                            placeholder="Select branch"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    {canManageProducts && (
                        <PrimaryButton onClick={() => openModal("add")}>+ Add Product</PrimaryButton>
                    )}
                </div>
            </div>
            {/* Header + View Toggle */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>Products List</h2>
                <ViewToggle
                    viewMode={viewMode}
                    onToggle={() => setViewMode(viewMode === "table" ? "grid" : "table")}
                />
            </div>

            {/* Product Display */}
            {error ? (
                <EmptyState
                    icon={Package}
                    title="Failed to Load Products"
                    message="Check your internet connection and try again."
                    onRetry={loadProducts}
                    retryLabel="Reload"
                />
            ) : loading ? (
                <div className="rounded-lg border border-gray-200 bg-white overflow-auto max-h-[calc(100vh-340px)] min-h-[300px]">
                    <TableLoadingSkeleton
                        mode={viewMode}
                        columnLabels={productSkeletonConfig.columnLabels}
                        columnTypes={productSkeletonConfig.columnTypes}
                        rowCount={4}
                        gridCount={8}
                        columnTemplate={productSkeletonConfig.columnTemplate}
                    />
                </div>
            ) : products.length === 0 ? (
                <EmptyState
                    icon={Package}
                    title="No Products Found"
                    message={searchTerm ? "No products match your search." : "No products available yet."}
                />
            ) : viewMode === "table" ? (
                <ProductTable
                    products={products}
                    onEdit={canManageProducts ? (p) => openModal("edit", p) : undefined}
                    onDelete={canManageProducts ? (p) => openModal("delete", p) : undefined}
                    uploadsBaseURL={uploadsBaseURL}
                    showActions={canManageProducts}
                    showBranchPrice={showBranchPrice}
                />
            ) : (
                <ProductGrid
                    products={products}
                    onEdit={canManageProducts ? (p) => openModal("edit", p) : undefined}
                    onDelete={canManageProducts ? (p) => openModal("delete", p) : undefined}
                    uploadsBaseURL={uploadsBaseURL}
                    showActions={canManageProducts}
                />
            )}

            {/* Pagination - Only show when no error and data exists */}
            {!error && (
                <div className="flex flex-col items-center gap-2 mt-2">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                    <p className="text-sm" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                        Showing {products.length === 0 ? 0 : (currentPage - 1) * productsPerPage + 1}
                        – {Math.min(currentPage * productsPerPage, totalProducts)}{' '}
                        of {totalProducts} items | Page {currentPage} of {totalPages}
                    </p>
                </div>
            )}

            {/* Add / Edit / Delete Modals */}
            {modal.open && modal.type === "add" && (
                <AddProductModal
                    editData={null}
                    onClose={closeModal}
                    onSubmit={handleAddEditProduct}
                    branches={branches}
                    isOpen
                />
            )}
            {modal.open && modal.type === "edit" && (
                <AddProductModal
                    editData={modal.product}
                    onClose={closeModal}
                    onSubmit={(data) => handleAddEditProduct(data, true)}
                    branches={branches}
                    isOpen
                />
            )}
            {modal.open && modal.type === "delete" && (
                <DeleteModal
                    isOpen
                    onClose={closeModal}
                    onConfirm={handleDeleteProduct}
                    title="Delete Product"
                    message={`Are you sure you want to delete "${modal.product?.name}"?`}
                    confirmText="Yes"
                    cancelText="No"
                    type="delete"
                />
            )}
        </div>
    );
};

export default ProductList;
